import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// import InputMask from 'react-input-mask';
import { Oval } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { resetTickets } from 'store/ticket-service/ticketSlice';
import {
	deleteCustomer,
	deleteUserAccount,
	updateUserIcon,
	updateUserProfile,
} from 'store/user-service/asyncActions';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { userProfileSchema } from 'core/helpers/yupSchemas';
import { UserData } from 'core/types/base';

import { ChangePasswordModal } from 'components/Profile/ChangePasswordModal';
import { DeleteAccountModal } from 'components/Profile/DeleteAccountModal';
import { fieldNames } from 'components/Profile/helpers';
import { SubmitButton } from 'components/Profile/styled';

import { ReactComponent as Camera } from 'assets/icons/camera.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';

import { ChangeEmail } from './ChangeEmail';
import * as Styled from './styled';

export const User = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: signUpPageTranslate } = useTranslation(NamespacesList['signUpPage']);
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);

	const inputRef = useRef<null | HTMLInputElement>(null);

	const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || ''));
	const [icon, setIcon] = useState(user?.avatarLink);
	const [isIconUploading, setIsIconUploading] = useState(false);
	const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
	const [isOpenChangePasswordModal, setIsOpenChangePasswordModal] = useState(false);
	const [updatingLoading, setUpdatingLoading] = useState(false);

	const {
		handleSubmit,
		register,
		formState: { errors, isDirty },
		setValue,
		reset,
	} = useForm({
		resolver: yupResolver(userProfileSchema()),
	});

	const getUserData = () => {
		fieldNames.forEach((fieldName) => {
			if (fieldName === 'phoneNumber' && user[fieldName]) {
				setValue(
					fieldName,
					user[fieldName].replace(/^(\+\d{3})(\d{2})(\d{3})(\d{2})(\d{2})$/, '$1 $2 $3 $4 $5')
				);
			} else {
				setValue(fieldName, user[fieldName]);
			}
		});
	};

	useEffect(() => {
		getUserData();
	}, [user]);

	const getBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = () => {
				const base64Result = reader.result as string;
				resolve(base64Result);
			};

			reader.onerror = (error) => {
				reject(error);
			};

			reader.readAsDataURL(file);
		});
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const selectedFile = e.target.files?.[0];

			if (selectedFile) {
				const localStorageUser = JSON.parse(localStorage.getItem('user') || '');

				const base64Result = await getBase64(selectedFile);

				setIsIconUploading(true);

				const updatedIcon = await dispatch(
					updateUserIcon({ id: user.id, img: base64Result.replace('data:image/png;base64,', '') })
				).unwrap();

				setIcon(updatedIcon);

				setIsIconUploading(false);

				await localStorage.setItem(
					'user',
					JSON.stringify({ ...localStorageUser, avatarLink: updatedIcon })
				);

				window.dispatchEvent(new Event('storage'));
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleActionDeleteModal = () => {
		setIsOpenDeleteModal((prev) => !prev);
	};

	const handleActionChangePasswordModal = () => {
		setIsOpenChangePasswordModal((prev) => !prev);
	};

	const handleUpdate = async (data: any) => {
		setUpdatingLoading(true);
		try {
			const updatedData = await dispatch(updateUserProfile({ id: user.id, data })).unwrap();

			setUser((prev: UserData) => ({ ...prev, ...data }));

			const localStorageUser = JSON.parse(localStorage.getItem('user') || '');
			const updatedLocalStorageUser = { ...localStorageUser, ...updatedData };
			await localStorage.setItem('user', JSON.stringify(updatedLocalStorageUser));

			window.dispatchEvent(new Event('storage'));

			await reset();

			getUserData();
		} catch (error: any) {
			console.log(error);
		} finally {
			setUpdatingLoading(false);
		}
	};

	const selectFile = () => {
		if (inputRef && inputRef.current) {
			inputRef.current.click();
		}
	};

	const handleDeleteAccount = async () => {
		try {
			const afterDelete = () => {
				dispatch(resetTickets());
				Object.keys(localStorage).forEach((key) => {
					if (key !== 'i18nextLng') {
						localStorage.removeItem(key);
					}
				});
				handleActionDeleteModal();
				navigate(ROUTES.LOGIN);
			};
			await dispatch(deleteCustomer()).unwrap();
			await dispatch(deleteUserAccount({ id: user.id, function: afterDelete })).unwrap();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<>
			<Styled.ChangeImage>
				<input
					ref={inputRef}
					type="file"
					id="file"
					onChange={handleFileChange}
					accept="image/png"
					hidden
				/>
				{isIconUploading ? (
					<Styled.LoaderContainer>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Styled.LoaderContainer>
				) : !icon ? (
					<Styled.UploadAvatar role="presentation" onClick={selectFile}>
						<Camera />
					</Styled.UploadAvatar>
				) : (
					<Styled.UserAvatar
						alt="user-avatar"
						src={icon || ''}
						sx={{ width: '106px', height: '106px' }}
						onClick={selectFile}
					/>
				)}
			</Styled.ChangeImage>
			<form onSubmit={handleSubmit(handleUpdate)} style={{ width: '100%' }}>
				<Styled.Inputs className="rtl-able">
					<FormControl fullWidth error={!!errors.firstName}>
						<Typography sx={{ fontSize: '12px' }}>
							{signUpPageTranslate('text.firstName')}
						</Typography>
						<TextField
							inputProps={{ style: { padding: '10px 12px' } }}
							placeholder={signUpPageTranslate('placeholders.firstName') as string}
							sx={{
								height: '44px',
								'& .MuiOutlinedInput-root': {
									borderRadius: '8px',
								},
							}}
							{...register('firstName')}
						/>
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								marginRight: '12px',
								marginTop: '0',
							}}
						>
							{errors.firstName?.message}
						</FormHelperText>
					</FormControl>
					<FormControl fullWidth error={!!errors.lastName}>
						<Typography sx={{ fontSize: '12px' }}>
							{signUpPageTranslate('text.lastName')}
						</Typography>
						<TextField
							inputProps={{ style: { padding: '10px 12px' } }}
							placeholder={signUpPageTranslate('placeholders.lastName') as string}
							sx={{
								height: '44px',
								'& .MuiOutlinedInput-root': {
									borderRadius: '8px',
								},
							}}
							{...register('lastName')}
						/>
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								marginRight: '12px',
								marginTop: '0',
							}}
						>
							{errors.lastName?.message}
						</FormHelperText>
					</FormControl>
				</Styled.Inputs>
				{updatingLoading ? (
					<SubmitButton
						type="submit"
						variant="contained"
						color="primary"
						disabled={updatingLoading}
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</SubmitButton>
				) : (
					<SubmitButton
						type="submit"
						variant="contained"
						color="primary"
						// disabled
						disabled={!isDirty}
					>
						{commonTranslate('buttons.save-changes')}
					</SubmitButton>
				)}
			</form>
			<ChangeEmail />
			<Styled.ChangePassword className="rtl-able">
				<Styled.ChangePasswordInfo>
					<Typography variant="h4">{profileTranslate('text.changePasswordTitle')}</Typography>
					<Typography>{profileTranslate('text.changePasswordDescription')}</Typography>
				</Styled.ChangePasswordInfo>
				<Button onClick={handleActionChangePasswordModal}>
					{commonTranslate('buttons.change-password')}
				</Button>
			</Styled.ChangePassword>
			<Styled.DeleteButton
				sx={{ justifySelf: 'flex-end' }}
				className="rtl-able"
				onClick={handleActionDeleteModal}
			>
				<Trash />
				{commonTranslate('buttons.delete-account')}
			</Styled.DeleteButton>
			<DeleteAccountModal
				open={isOpenDeleteModal}
				handleClose={handleActionDeleteModal}
				handleConfirm={handleDeleteAccount}
			/>
			<ChangePasswordModal
				open={isOpenChangePasswordModal}
				onClose={handleActionChangePasswordModal}
			/>
		</>
	);
};
